import type {
  ArchiveHubResponse,
  BlockHubProfessionalResponse,
  CreateHubRequest,
  DecideHubBody,
  DecideHubRevisionBody,
  GetHubCalendarParams,
  HubCalendar,
  HubManagerView,
  HubViewer,
  HubsAdminPage,
  ImproveHubDescriptionRequest,
  ListHubProfessionalsForManagerResponse,
  ListHubProfessionalsResponse,
  ListHubsAdminParams,
  ListMyHubsResponse,
  PublicHub,
  RemoveHubManagerResponse,
  SearchHubsResponse,
  UnblockHubProfessionalResponse,
  UpdateHubAddressBody,
  UpdateHubBody,
  UpdateHubResponse
} from '@api/models';
import { ImproveHubDescriptionRequestLocale } from '@api/models';
import type { Locale } from 'next-intl';

import { ApiClient } from '@website/lib/api';
import { nullOn404 } from '@website/lib/api/apiError';

/**
 * The Hub as the public sees it, or null when it is missing or not approved (the API answers 404).
 * Other failures throw, so a cached page is kept rather than replaced by a not-found.
 */
export function getPublicHub(id: string, options?: RequestInit): Promise<PublicHub | null> {
  return nullOn404(ApiClient.getHub(id, options));
}

export function getHubCalendar(
  id: string,
  from: GetHubCalendarParams['from'],
  to: GetHubCalendarParams['to'],
  options?: RequestInit
): Promise<HubCalendar> {
  return ApiClient.getHubCalendar(id, { from, to }, options);
}

/**
 * The first date `professionalId` has a session still to come at the Hub from `from` (from today when
 * `from` is earlier), within the calendar's window (a year ahead); null when they have none. `from`
 * starts the search; the one day of entries asked with it is not read.
 */
export async function getHubNextSessionOf(
  id: string,
  professionalId: string,
  from: string,
  options?: RequestInit
): Promise<string | null> {
  const { nextSessionDate } = await ApiClient.getHubCalendar(id, { from, to: from, professionalId }, options);
  return nextSessionDate;
}

/** The public Residents (approved, not blocked), as the viewer may see them. */
export function getHubProfessionals(id: string, options?: RequestInit): Promise<ListHubProfessionalsResponse> {
  return ApiClient.listHubProfessionals(id, options);
}

/** Everyone placed at the Hub, with their profile status and activities, and the blocked list (Managers and admins). */
export function getHubPlacedProfessionals(
  id: string,
  options?: RequestInit
): Promise<ListHubProfessionalsForManagerResponse> {
  return ApiClient.listHubProfessionalsForManager(id, options);
}

/**
 * Whether the signed-in caller manages the Hub, whether its Managers blocked them, and their profile's
 * review status; null for a Hub they cannot see (404, a Hub not approved), which the page then treats
 * like a signed-out visit.
 */
export function getHubViewer(id: string, options?: RequestInit): Promise<HubViewer | null> {
  return nullOn404(ApiClient.getHubViewer(id, options));
}

/** Approved Hubs by name or city, minus those that blocked the caller; an empty query lists them alphabetically. */
export function searchHubs(query: string, options?: RequestInit): Promise<SearchHubsResponse> {
  return ApiClient.searchHubs({ query }, options);
}

/** Removes a professional's placements at the Hub and refuses new ones. The reason stays with the Managers. */
export function blockHubProfessional(
  id: string,
  professionalId: string,
  reason?: string,
  options?: RequestInit
): Promise<BlockHubProfessionalResponse> {
  const trimmed = reason?.trim();
  return ApiClient.blockHubProfessional(id, { professionalId, ...(trimmed ? { reason: trimmed } : {}) }, options);
}

export function unblockHubProfessional(
  id: string,
  professionalId: string,
  options?: RequestInit
): Promise<UnblockHubProfessionalResponse> {
  return ApiClient.unblockHubProfessional(id, professionalId, options);
}

export function getMyHubs(options?: RequestInit): Promise<ListMyHubsResponse> {
  return ApiClient.listMyHubs(options);
}

export function getHubForManager(id: string, options?: RequestInit): Promise<HubManagerView> {
  return ApiClient.getHubForManager(id, options);
}

export function createHub(body: CreateHubRequest, options?: RequestInit): Promise<HubManagerView> {
  return ApiClient.createHub(body, options);
}

export function updateHub(id: string, body: UpdateHubBody, options?: RequestInit): Promise<UpdateHubResponse> {
  return ApiClient.updateHub(id, body, options);
}

export function archiveHub(id: string, options?: RequestInit): Promise<ArchiveHubResponse> {
  return ApiClient.archiveHub(id, options);
}

export function addHubManager(id: string, email: string, options?: RequestInit): Promise<HubManagerView> {
  return ApiClient.addHubManager(id, { email }, options);
}

export function removeHubManager(id: string, userId: string, options?: RequestInit): Promise<RemoveHubManagerResponse> {
  return ApiClient.removeHubManager(id, userId, options);
}

export function listHubsAdmin(params?: ListHubsAdminParams, options?: RequestInit): Promise<HubsAdminPage> {
  return ApiClient.listHubsAdmin(params, options);
}

export function decideHub(id: string, body: DecideHubBody, options?: RequestInit): Promise<HubManagerView> {
  return ApiClient.decideHub(id, body, options);
}

export function decideHubRevision(
  id: string,
  body: DecideHubRevisionBody,
  options?: RequestInit
): Promise<HubManagerView> {
  return ApiClient.decideHubRevision(id, body, options);
}

export function updateHubAddress(
  id: string,
  body: UpdateHubAddressBody,
  options?: RequestInit
): Promise<HubManagerView> {
  return ApiClient.updateHubAddress(id, body, options);
}

/** The draft's language for each of the site's locales (all among the API's: a new locale fails to compile here). */
const AI_LOCALE: Record<Locale, ImproveHubDescriptionRequestLocale> = {
  en: ImproveHubDescriptionRequestLocale.en,
  fr: ImproveHubDescriptionRequestLocale.fr
};

/**
 * The Hub's description rewritten by AI, or a first draft from its name and address when `description` is empty.
 * `locale` is the page's: the draft's language.
 */
export async function improveHubDescription(
  params: Omit<ImproveHubDescriptionRequest, 'locale'> & { locale?: Locale }
): Promise<string> {
  const { locale, ...input } = params;
  const { description } = await ApiClient.improveHubDescription({
    ...input,
    locale: locale && AI_LOCALE[locale]
  });
  return description;
}
